import * as React from "react"
import { cn } from "../lib/utils"
import { X } from "lucide-react"

type ToastType = "success" | "error" | "info"
type Toast = { id: string; message: string; type: ToastType }

const ToastContext = React.createContext<{
  toast: (message: string, type?: ToastType) => void
} | null>(null)

/** Toast provider. Manages a list of active toasts and auto-dismisses them after 4 seconds. */
export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = React.useState<Toast[]>([])

  const toast = React.useCallback((message: string, type: ToastType = "info") => {
    const id = Math.random().toString(36).slice(2)
    setToasts((prev) => [...prev, { id, message, type }])
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id))
    }, 4000)
  }, [])

  const remove = React.useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      <Toaster toasts={toasts} onRemove={remove} />
    </ToastContext.Provider>
  )
}

/** Hook to push a toast from anywhere in the tree. Throws if used outside the provider. */
export function useToast() {
  const ctx = React.useContext(ToastContext)
  if (!ctx) throw new Error("useToast must be used within ToastProvider")
  return ctx
}

/** Renders the stack of toast notifications in the bottom-right corner. */
function Toaster({ toasts, onRemove }: { toasts: Toast[]; onRemove: (id: string) => void }) {
  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2">
      {toasts.map((t) => (
        <div
          key={t.id}
          className={cn(
            "flex items-center gap-3 rounded-md px-4 py-3 shadow-lg text-sm font-medium animate-in fade-in slide-in-from-bottom-2",
            t.type === "error" && "bg-destructive text-white",
            t.type === "success" && "bg-emerald-600 text-white",
            t.type === "info" && "bg-primary text-primary-foreground"
          )}
        >
          <span className="flex-1">{t.message}</span>
          <button onClick={() => onRemove(t.id)} className="opacity-80 hover:opacity-100">
            <X className="h-4 w-4" />
          </button>
        </div>
      ))}
    </div>
  )
}
